import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookA, BookOpen, CalendarDays, Compass, Eye, EyeOff, GraduationCap, Languages, Library, Lightbulb, Map as MapIcon,
  Medal, NotebookText, Pencil, Rocket, Target, Trash2, Trophy, User,
} from 'lucide-react';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import { useI18n } from '../../i18n/I18nContext';
import { formatDate, formatMoney, formatNumber } from '../../utils/datetime';
import { pickPlural, pluralIndex } from './plural';
import './CourseListCard.css';

// The plural forms live in ./plural (the list's result count needs them too); re-exported for the tests.
export { pickPlural, pluralIndex };

// A course with no cover of its own is drawn, not left blank: an icon on a soft tone. The tone and
// the exact icon come from the course id (a row of covers is never one flat colour, and a course
// keeps its look between visits); the icon family says the level: books and words to begin with,
// then aiming and exploring, then the rocket and the trophies.
const COVER_TONES = ['info', 'coral', 'teal', 'purple', 'success', 'brand'];
const COVER_ICONS = {
  BEGINNER: [BookOpen, Languages, NotebookText, BookA],
  INTERMEDIATE: [Target, MapIcon, Compass],
  ADVANCED: [Rocket, Trophy, Medal],
};
const COVER_ICONS_ANY = [Lightbulb, GraduationCap, Library];
const LEVEL_KEYS = { BEGINNER: 'beginner', INTERMEDIATE: 'intermediate', ADVANCED: 'advanced' };

function hashOf(id) {
  const key = String(id ?? '');
  let hash = 7;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return hash;
}

export function coverToneFor(id) {
  return COVER_TONES[hashOf(id) % COVER_TONES.length];
}

export function coverIconFor(level, id) {
  const family = COVER_ICONS[String(level || '').toUpperCase()] || COVER_ICONS_ANY;
  // The icon draws on other bits of the hash than the tone, so the two vary independently.
  return family[(hashOf(id) >>> 4) % family.length];
}

/**
 * One course in the catalogue grid. The whole card is a link to the course workspace (the title
 * carries the link, stretched over the card); what else can be done with it lives in a "⋯" menu
 * in the footer: edit, publish / unpublish (administrators), delete (administrators).
 *
 * `preview` renders the same card without a link or menu: the live preview on the create page.
 */
function CourseListCard({
  course,
  canDelete = false,
  canPublish = false,
  onDelete,
  onTogglePublish,
  preview = false,
}) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  // The cover that failed to load, not a flag: a new cover (the create page's live preview) gets
  // its own chance instead of staying on the drawn one for good.
  const [failedImage, setFailedImage] = useState(null);

  const title = course.title || '';
  const editHref = `/courses/edit/${course.id}`;
  const showImg = Boolean(course.image) && failedImage !== course.image;
  const isPublished = Boolean(course.isPublished);

  const counts = [
    ['cardLessons', course.lessonsTotal ?? 0],
    ['cardStudents', course.enrollmentsTotal ?? 0],
    ['cardTests', course.testsTotal ?? 0],
  ].map(([key, n]) => pickPlural(t(`courses.${key}`, { n: formatNumber(n, { decimals: 0 }) }), n, lang));

  const levelKey = LEVEL_KEYS[String(course.level || '').toUpperCase()];
  const levelLabel = levelKey ? t(`courseCreate.levels.${levelKey}`) : (course.level || null);
  // The category is the teacher's own word ("languages"): shown in sentence case like every label.
  const categoryLabel = course.category ? course.category.charAt(0).toUpperCase() + course.category.slice(1) : null;
  const created = course.createdAt ? formatDate(course.createdAt) : null;
  const showOwner = !course.isMine && Boolean(course.ownerName);
  const CoverIcon = coverIconFor(course.level, course.id);

  const mayPublish = Boolean(canPublish && onTogglePublish);
  const mayDelete = Boolean(canDelete && onDelete);
  // Edit is already the card itself, so a menu with nothing but "Edit" in it would be noise.
  const showMenu = !preview && (mayPublish || mayDelete);
  const menuItems = [
    { key: 'edit', label: t('courses.menuEdit'), icon: <Pencil />, onSelect: () => navigate(editHref) },
    ...(mayPublish ? [{
      key: 'publish',
      label: isPublished ? t('courses.menuUnpublish') : t('courses.menuPublish'),
      icon: isPublished ? <EyeOff /> : <Eye />,
      onSelect: () => onTogglePublish(course),
    }] : []),
    ...(mayDelete ? [
      { key: 'divider', separator: true },
      { key: 'delete', label: t('courses.delete'), icon: <Trash2 />, tone: 'danger', onSelect: () => onDelete(course) },
    ] : []),
  ];

  const Shell = preview ? 'div' : 'article';

  return (
    <Shell className={`section-card courses-card${preview ? '' : ' is-interactive'}`}>
      <div className="courses-card-cover" data-tone={coverToneFor(course.id)}>
        {showImg ? (
          <img className="courses-card-img" src={course.image} alt="" loading="lazy" onError={() => setFailedImage(course.image)} />
        ) : (
          <span className="courses-card-art" aria-hidden="true">
            <span className="courses-card-icon"><CoverIcon /></span>
          </span>
        )}
        <span className={`ds-badge ds-badge--dot courses-card-status ${isPublished ? 'is-live' : 'is-draft'}`}>
          {isPublished ? t('courses.statusPublished') : t('courses.statusDraft')}
        </span>
      </div>

      <div className="courses-card-body">
        <h2 className="courses-card-title" title={title}>
          {preview
            ? title
            : <Link to={editHref} className="courses-card-link">{title}</Link>}
        </h2>

        {(levelLabel || categoryLabel) && (
          <div className="courses-card-badges">
            {levelLabel && <span className="ds-badge ds-badge-brand">{levelLabel}</span>}
            {categoryLabel && <span className="ds-badge ds-badge-neutral" title={categoryLabel}>{categoryLabel}</span>}
          </div>
        )}

        {course.description && <p className="courses-card-desc">{course.description}</p>}

        <p className="courses-card-stats">
          {counts.map((label, i) => <span key={i}>{label}</span>)}
        </p>

        {(created || showOwner) && (
          <p className="courses-card-meta">
            {created && (
              <span className="courses-card-meta-item">
                <CalendarDays size={14} aria-hidden="true" />
                {created}
              </span>
            )}
            {showOwner && (
              <span className="courses-card-meta-item courses-card-owner" title={course.ownerName}>
                <User size={14} aria-hidden="true" />
                <span className="courses-card-owner-text">{course.ownerName}</span>
              </span>
            )}
          </p>
        )}
      </div>

      <div className="courses-card-foot">
        <span className="courses-card-price">{formatMoney(course.price, course.currency)}</span>
        {showMenu && (
          <ActionMenu
            className="courses-card-menu"
            ariaLabel={t('courses.menuAria', { title })}
            items={menuItems}
          />
        )}
      </div>
    </Shell>
  );
}

export default CourseListCard;
